import Chevron from '@img/icons/chevron.svg';
import ZoomIcon from '@img/icons/zoom.svg';
import { RootState } from '@state/ducks';
import ImageZoomModal from '@stories/Components/Media/ImageZoomModal/ImageZoomModal';
import LazyImage from '@stories/Elements/Media/LazyImage/LazyImage';
import Loader from '@stories/Elements/Misc/Loader/Loader.hybrid';
import { Grid } from '@stories/Settings/Grids/Grid.styles';
import React, { FunctionComponent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';
import { ProductMediaItem } from '../ProductMediaLayout/ProductMediaLayout';
import { ProductMediaMobileStyles as S } from './ProductMediaMobile.styles';

export interface ProductMediaMobileProps {
  selectedVariantId: number | null;
}

const ProductMediaMobile: FunctionComponent<ProductMediaMobileProps> = (props) => {
  const media = useSelector((x: RootState) => x.productDetail.assets);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const sliderRef = useRef<Slider>(null);
  const [zoomModalInitialIndex, setZoomModalInitialIndex] = React.useState<number>(-1);

  const imagesOnly: ProductMediaItem[] = useMemo(() => {
    if (!media) {
      return [];
    }

    return [...media.app, ...media.hero];
  }, [media]);

  const allMedia = useMemo(() => {
    if (!media) {
      return [];
    }

    const _allMedia: ProductMediaItem[] = [...media.app, ...media.hero];

    if (media.snippet) {
      _allMedia.push(media.snippet);
    }

    if (media.embed) {
      _allMedia.push(media.embed);
    }

    return _allMedia;
  }, [media]);

  if (!media) {
    return null;
  }

  const preventVerticalSwipe = (e: Event): boolean | undefined => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!sliderRef.current || (sliderRef.current as any).innerSlider.state.swiping === false) {
      return;
    }

    e.preventDefault();
    e.returnValue = false;
    return false;
  };

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const slickList = containerRef.current.querySelector('.slick-list');

    if (!slickList) {
      return;
    }

    window.addEventListener('touchmove', preventVerticalSwipe, { passive: false });

    return () => {
      window.removeEventListener('touchmove', preventVerticalSwipe);
    };
  }, []);

  useEffect(() => {
    if (props.selectedVariantId === null || sliderRef.current === null) {
      return;
    }

    sliderRef.current.slickGoTo(0, true);
  }, [props.selectedVariantId]);

  const handleBeforeChange = (currentIndex: number, nextIndex: number): void => {
    setActiveIndex(nextIndex);

    if (!containerRef.current) {
      return;
    }

    // I think slick is removing my video ref so
    // have to get the current one like this :(
    const video = containerRef.current.querySelector<HTMLVideoElement>('.slick-current video');

    if (!video) {
      return;
    }

    video.pause();
  };

  const handleAfterChange = (): void => {
    if (!containerRef.current) {
      return;
    }

    // I think slick is removing my video ref so
    // have to get the current one like this :(
    const video = containerRef.current.querySelector<HTMLVideoElement>('.slick-current video');

    if (!video) {
      return;
    }

    video.play();
  };

  const renderImage = (media: ProductMediaItem, preload?: boolean, zoomIndex?: number): ReactNode => {
    const shouldRenderZoomButton = zoomIndex !== undefined;

    return (
      <S.Slide key={media.imageUrl}>
        <LazyImage src={media.imageUrl} alt={media.description || ''} width={500} height={500} preload={preload} />

        {shouldRenderZoomButton ? (
          <S.ZoomButton onClick={() => setZoomModalInitialIndex(zoomIndex ?? 0)}>
            <ZoomIcon />
          </S.ZoomButton>
        ) : null}
      </S.Slide>
    );
  };

  const renderVideo = (media: ProductMediaItem): ReactNode => {
    return (
      <S.Slide key={media.imageUrl}>
        <video
          muted
          controls={false}
          playsInline
          loop
          src={media.imageUrl}
          preload="metadata"
          disablePictureInPicture
        />
      </S.Slide>
    );
  };

  const renderEmbed = (media: ProductMediaItem): ReactNode => {
    if (!media.url) {
      return;
    }

    // Currently the embed is always the last item so np
    const isActive = activeIndex === allMedia.length - 1;

    return (
      <S.Slide key={media.imageUrl}>
        <S.IFrameWrapper>
          <Loader />

          {isActive ? <iframe src={media.url} allowFullScreen /> : null}
        </S.IFrameWrapper>
      </S.Slide>
    );
  };

  return (
    <>
      <S.Container ref={containerRef}>
        {allMedia.length > 0 && (
          <S.Carousel>
            {allMedia.length > 1 && (
              <S.AbsoluteOverlay>
                <Grid.Default>
                  <S.Arrow
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      sliderRef.current?.slickPrev();
                    }}
                  >
                    <Chevron />
                  </S.Arrow>

                  <S.Arrow
                    isNext={true}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      sliderRef.current?.slickNext();
                    }}
                  >
                    <Chevron />
                  </S.Arrow>
                </Grid.Default>
              </S.AbsoluteOverlay>
            )}
            {allMedia.length > 1 ? (
              <Slider
                ref={sliderRef}
                slidesToShow={1}
                swipeToSlide={true}
                arrows={false}
                dots={false}
                beforeChange={handleBeforeChange}
                afterChange={handleAfterChange}
              >
                {imagesOnly.map((x, i) => renderImage(x, i === 0, i))}
                {media.snippet ? renderVideo(media.snippet) : null}
                {media.embed ? renderEmbed(media.embed) : null}
              </Slider>
            ) : (
              <div>{renderImage(allMedia[0])}</div>
            )}
          </S.Carousel>
        )}

        {allMedia.length > 1 ? (
          <S.Indicators>
            {allMedia.map((x, i) => (
              <S.Indicator key={x.imageUrl} isActive={i === activeIndex}></S.Indicator>
            ))}
          </S.Indicators>
        ) : null}
      </S.Container>

      {zoomModalInitialIndex > -1 ? (
        <ImageZoomModal
          images={imagesOnly.map((x) => x.imageUrl)}
          initialIndex={zoomModalInitialIndex}
          onClose={() => setZoomModalInitialIndex(-1)}
        />
      ) : null}
    </>
  );
};

export default ProductMediaMobile;
